/**
 * CTE Build Plan PDF Export
 * Generates a professional PDF report from the Build Planner data.
 * Layout helpers live in the shared writer (src/utils/pdf-writer.js).
 */

import {
  createDoc, checkPage, drawHeader, drawSectionTitle, drawKeyValue,
  drawTable, drawWrappedText, stripMarkdown, drawDisclaimerFooter,
  MUTED_COLOR, TEXT_COLOR, PAGE_W, MARGIN, CONTENT_W
} from '../../utils/pdf-writer';

// This report uses a slightly tighter label column than the shared default.
const LABEL_W = 45;

export function generateBuildPlanPDF(data) {
  const doc = createDoc();

  let y = drawHeader(doc, 'CTE Build Plan Report');

  // --- Project Summary ---
  y = drawSectionTitle(doc, y, 'Project Summary');
  y = drawKeyValue(doc, y, 'Part Material', data.partMaterial || '-', MARGIN, LABEL_W);
  y = drawKeyValue(doc, y, 'Cure Temp', data.partCureTemp ? `${data.partCureTemp} deg C` : '-', MARGIN, LABEL_W);
  y = drawKeyValue(doc, y, 'Mould Material', data.mouldMaterial || '-', MARGIN, LABEL_W);
  y = drawKeyValue(doc, y, 'Mould Tg', data.mouldTg !== null && data.mouldTg !== undefined ? `${data.mouldTg} deg C` : 'None', MARGIN, LABEL_W);
  y = drawKeyValue(doc, y, 'Mould Type', data.mouldType === 'female' ? 'Female (cavity)' : 'Male (plug)', MARGIN, LABEL_W);
  y = drawKeyValue(doc, y, 'Dimensions', `X: ${data.dims.x} mm   Y: ${data.dims.y} mm   Z: ${data.dims.z} mm`, MARGIN, LABEL_W);
  if (data.fileName) {
    y = drawKeyValue(doc, y, 'CAD File', data.fileName, MARGIN, LABEL_W);
  }
  y += 4;

  // --- Material Suitability ---
  if (data.suitability) {
    y = drawSectionTitle(doc, y, 'Material Suitability');
    const s = data.suitability;
    const riskLabel = s.riskLevel === 'green' ? 'OK' : s.riskLevel === 'amber' ? 'CAUTION' : 'HIGH RISK';
    y = drawKeyValue(doc, y, 'Risk Level', `${riskLabel} — ${s.riskNote}`, MARGIN, LABEL_W);
    y = drawKeyValue(doc, y, 'Z-Strain', `${s.zStrainPerM} um/m at cure temp`, MARGIN, LABEL_W);
    y += 4;
  }

  // --- Orientation Sweep ---
  if (data.sweepResults && data.sweepResults.length > 0) {
    y = drawSectionTitle(doc, y, 'Orientation Sweep');
    const rows = data.sweepResults.map(r => {
      const label = r.angle === 0 ? 'Flat' : r.angle === 90 ? 'On Side' : `${r.angle} deg`;
      const best = r.angle === data.bestAngle ? ' (best)' : '';
      const selected = r.angle === data.selectedAngle ? ' (selected)' : '';
      return [label, `${r.maxDispMM.toFixed(4)} mm`, `${best}${selected}`];
    });
    y = drawTable(doc, y, ['Build Angle', 'Max Distortion', 'Notes'], rows);
  }

  // --- Compensation Results ---
  if (data.compResult) {
    y = drawSectionTitle(doc, y, 'Compensation Results');
    const c = data.compResult;
    y = drawKeyValue(doc, y, 'Build Angle', c.buildAngle === 0 ? 'Flat print' : `${c.buildAngle} deg`, MARGIN, LABEL_W);
    y = drawKeyValue(doc, y, 'Max Compensation', `${c.maxCompMM.toFixed(4)} mm`, MARGIN, LABEL_W);
    y = drawKeyValue(doc, y, 'Avg Compensation', `${c.avgCompMM.toFixed(4)} mm`, MARGIN, LABEL_W);
    y = drawKeyValue(doc, y, 'Delta T', `${c.deltaT.toFixed(1)} deg C`, MARGIN, LABEL_W);
    y += 2;

    y = drawTable(doc, y,
      ['Axis', 'Strain (um/m)'],
      [
        ['X', c.strainDiag.x.toFixed(2)],
        ['Y', c.strainDiag.y.toFixed(2)],
        ['Z', c.strainDiag.z.toFixed(2)],
      ]
    );
  }

  // --- 3D Screenshots (mould viewer + build gizmo side by side) ---
  if (data.viewerImage || data.gizmoImage) {
    y = drawSectionTitle(doc, y, 'Build Orientation & Mould Preview');
    const imgH = 55;
    y = checkPage(doc, y, imgH + 6);

    if (data.gizmoImage && data.viewerImage) {
      const halfW = (CONTENT_W - 4) / 2;
      doc.addImage(data.gizmoImage, 'PNG', MARGIN, y, halfW, imgH);
      doc.addImage(data.viewerImage, 'PNG', MARGIN + halfW + 4, y, halfW, imgH);
    } else if (data.viewerImage) {
      doc.addImage(data.viewerImage, 'PNG', MARGIN, y, CONTENT_W, imgH);
    } else if (data.gizmoImage) {
      doc.addImage(data.gizmoImage, 'PNG', MARGIN, y, CONTENT_W, imgH);
    }

    y += imgH + 4;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED_COLOR);
    const caption = data.compResult
      ? `Build angle: ${data.compResult.buildAngle} deg | Distortion scale: visual only`
      : 'Build orientation as configured by user';
    doc.text(caption, PAGE_W / 2, y, { align: 'center' });
    doc.setTextColor(...TEXT_COLOR);
    y += 6;
  }

  // --- AI Build Plan ---
  if (data.aiPlan) {
    y = drawSectionTitle(doc, y, 'AI Build Plan');
    const plain = stripMarkdown(data.aiPlan);
    const paragraphs = plain.split('\n\n').filter(p => p.trim());
    for (const para of paragraphs) {
      y = drawWrappedText(doc, y, para.trim(), 9);
      y += 2;
    }
  }

  drawDisclaimerFooter(doc, y, 'CTE Build Plan Report');

  // --- Save ---
  const date = new Date().toISOString().split('T')[0];
  const safeMat = (data.mouldMaterial || 'Mould').replace(/[^a-z0-9]/gi, '_');
  doc.save(`RapidFusion_CTE_BuildPlan_${safeMat}_${date}.pdf`);
}
